import * as React from 'react'
import { Localized } from '@fluent/react'
import Role from '../../../../../api/role'
import { TeamPermission } from '../../../../../api/team'
import store from '../../../../../store'
import { addAlert } from '../../../../../store/actions/alerts'
import { confirmDialog } from '../../../../../helpers'
import TeamPermissions from '../../../../../components/TeamPermissions'
import Button from '../../../../../components/ui/Button'
import Input from '../../../../../components/ui/Input'
import LimitedUI from '../../../../../components/LimitedUI'

import './index.css'

interface RoleManagerProps {
  role: Role
  afterDelete: (role: Role) => void
}

interface RoleManagerState {
  isEditing: boolean
  roleName: string
  permissions: TeamPermission[]
}

class RoleManager extends React.Component<RoleManagerProps> {
  state: RoleManagerState = {
    isEditing: false,
    roleName: this.props.role.name,
    permissions: this.props.role.permissions || [],
  }

  private toggleEditMode = () => {
    this.setState((prevState: RoleManagerState) => ({ isEditing: !prevState.isEditing }))
  }

  private openRemoveRoleDialog = async () => {
    const res = await confirmDialog({
      title: 'role-manager-delete-title',
      vars: { name: this.props.role.name },
      buttons: {
        cancel: 'role-manager-delete-cancel',
        confirm: 'role-manager-delete-confirm',
      },
      showCloseButton: false,
    })

    if (res === 'confirm') {
      this.removeRole()
    }
  }

  private removeRole = () => {
    this.props.role.archive()
      .then(() => {
        this.props.afterDelete(this.props.role)
        store.dispatch(addAlert('success', 'role-manager-delete-success', {
          name: this.props.role.name,
        }))
      })
      .catch(e => {
        store.dispatch(addAlert('error', 'role-manager-delete-error', {
          details: e.response.data.error,
        }))
      })
  }

  private onRoleNameChange = (roleName: string) => {
    this.setState({ roleName })
  }

  private handleChange = (permissions: TeamPermission[]) => {
    this.setState({ permissions })
  }

  private updateRole = (e: React.FormEvent) => {
    e.preventDefault()

    const { roleName, permissions } = this.state

    const data: { name?: string, permissions?: TeamPermission[] } = {}
    if (roleName !== this.props.role.name) {
      data.name = roleName
    }
    if (JSON.stringify(permissions) !== JSON.stringify(this.props.role.permissions)) {
      data.permissions = permissions
    }

    this.props.role.update(data)
      .then(r => {
        store.dispatch(addAlert('success', 'role-manager-update-success', {
          name: this.props.role.name,
        }))
      })
      .catch(e => {
        store.dispatch(addAlert('error', 'role-manager-update-error', {
          details: e.response.data.error,
        }))
      })
  }

  private cancelEditing = () => {
    this.setState({
      isEditing: false,
      roleName: this.props.role.name,
      permissions: this.props.role.permissions,
    })
  }

  componentDidUpdate(prevProps: RoleManagerProps) {
    const prevRole = prevProps.role
    const currRole = this.props.role

    if (prevRole.name !== currRole.name) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ roleName: currRole.name })
    }
    if (JSON.stringify(prevRole.permissions) !== JSON.stringify(currRole.permissions)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ permissions: currRole.permissions })
    }
  }

  public render() {
    const { isEditing, roleName, permissions } = this.state
    const role = this.props.role

    return (
      <div className="role-manager">
        <div className="role-manager__header">
          <span className="role-manager__name">
            {
              isEditing ?
                <Input
                  type="text"
                  l10nId="role-manager-name"
                  value={roleName}
                  onChange={this.onRoleNameChange}
                  required
                />
                : role.name
            }
          </span>
          <LimitedUI team={role.team} permissions="role:edit">
            <span className="role-manager__controls">
              <Button l10nId="role-manager-edit" clickHandler={this.toggleEditMode}>
                Edit
              </Button>
              <Button l10nId="role-manager-remove" type="danger" clickHandler={this.openRemoveRoleDialog}>
                Remove
              </Button>
            </span>
          </LimitedUI>
        </div>
        <div
          className={`role-manager__content ${isEditing ? 'active' : ''}`}
        >
          <form onSubmit={this.updateRole}>
            <TeamPermissions
              selected={permissions}
              onChange={this.handleChange}
            />
            <Localized id="role-manager-update-confirm" attrs={{ value: true }}>
              <input type="submit" value="Update role" disabled={!roleName} />
            </Localized>
            <Button l10nId="role-manager-update-cancel" type="danger" clickHandler={this.cancelEditing}>
              Cancel
            </Button>
          </form>
        </div>
      </div>
    )
  }
}

export default RoleManager
